import { InputAdornment, TextField } from "@material-ui/core";
import classnames from "classnames";
import { History } from "history";
import debounce from "lodash/debounce";
import * as React from "react";
import ReactPaginate from "react-paginate";
import { connect } from "react-redux";
import { DEBOUNCE_DELAY } from "src/constants";
import CreateEntityModal from "src/createEntityModal/CreateEntityModal";
import { getDocListPaddingRight } from "src/documentList/helpers";
import { Entity } from "src/documentUpload/types";
import {
  isRequesting,
  transitionIfFinished
} from "src/requestProgress/helpers";
import { actions, RequestProgressState } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import {
  initialFinishedState,
  IsFinishedState
} from "src/requestProgress/types";
import { showAlertBanner } from "src/shared/alertBanner/actions";
import { ALERT_BANNER_TYPES } from "src/shared/alertBanner/reducer";
import LoadingSpinner from "src/shared/LoadingSpinner";
import QuiqButton from "src/shared/QuiqButton";
import { State } from "src/state";
import selectorsToProps from "src/utils/selectorsToProps";
import {
  getEntities,
  GetEntitiesActionType,
  updatePage,
  updateSearch
} from "./actions";
import "./EntityList.scss";
import { EntityListColumnHeader } from "./EntityListColumnHeader";
import { EntityListRow } from "./EntityListRow";
import { NoEntityResults } from "./NoEntityResults";
import PlusIcon from "./plus.svg";
import { SearchIcon } from "./SearchIcon";
import { getEntityListPage, getEntityListState } from "./selector";
import { ENTITY_COLUMNS, EntitySort } from "./types";

interface EntityListViewState {
  isCreateEntityModalOpen: boolean;
  isCreateEntityFinished: IsFinishedState;
  searchTerm: string;
  isSearchActive: boolean;
  isSearchFocused: boolean;
  entitySort: EntitySort;
  currentPage?: number;
}

interface EntitiesDataType {
  data: Entity[] | null;
  dataCount: number | null;
}

interface EntitiesType {
  result: EntitiesDataType;
}

interface EntityListViewProps {
  getEntities: GetEntitiesActionType;
  requestProgress: RequestProgressState;
  entities: EntitiesType;
  history: History;
  showAlertBanner: typeof showAlertBanner;
  updatePageFunc: typeof updatePage;
  currentPage: number;
  updateSearchFunc: typeof updateSearch | any;
}

export class EntityListView extends React.Component<
  EntityListViewProps,
  EntityListViewState
> {
  private headerPaddingRight = getDocListPaddingRight();
  constructor(props: EntityListViewProps) {
    super(props);
    this.state = {
      isCreateEntityModalOpen: false,
      isCreateEntityFinished: initialFinishedState,
      isSearchActive: false,
      isSearchFocused: false,
      entitySort: { sortColumn: ENTITY_COLUMNS.NAME, sortAscending: true },
      searchTerm: ""
    };
    this.fetchNewEntities = debounce(this.fetchNewEntities, DEBOUNCE_DELAY);
  }

  public componentWillMount() {
    this.props.getEntities();
  }

  public toggleCreateEntityModal = () => {
    this.setState(prevState => {
      return {
        isCreateEntityModalOpen: !prevState.isCreateEntityModalOpen
      };
    });
  };

  public showAlertBanner = (isSuccess: boolean) => {
    const type = isSuccess
      ? ALERT_BANNER_TYPES.SUCCESS
      : ALERT_BANNER_TYPES.ERROR;
    const message = isSuccess
      ? "Success - New Entity Created"
      : "Error - Entity Already Exists";

    const options = {
      type,
      message
    };

    this.props.showAlertBanner(options);
  };

  public componentWillReceiveProps() {
    const isFinishedState = transitionIfFinished(actions.createEntity);
    if (isFinishedState && isFinishedState.finished) {
      this.resetSearchTermAndFetch();
      this.toggleCreateEntityModal();
      this.showAlertBanner(isFinishedState.success);
    }
  }

  public updatePage = (params: any) => {
    this.props.updatePageFunc(params);
    this.props.getEntities({
      filter: this.state.searchTerm,
      sortColumn: this.state.entitySort.sortColumn,
      sortAscending: this.state.entitySort.sortAscending,
      getEntityListPage
    });
  };

  public pageCount = () => {
    const count =
      this.props.entities &&
      this.props.entities.result &&
      this.props.entities.result.dataCount &&
      this.props.entities.result.dataCount / 25;
    return count;
  };

  public render() {
    return (
      <div className="Entities__container">
        <div className="Entities__header">
          <div className="Entities__header__title">All Entities</div>
          <div className="Entities__header__actions">
            <div
              className={classnames("Entities__header__search", {
                "Entities__header__search--inactive": !this.state
                  .isSearchActive,
                "Entities__header__search--active": this.state.isSearchActive
              })}
              onMouseOver={this.expandSearch}
              onMouseOut={this.collapseSearch}
            >
              <TextField
                margin="normal"
                onFocus={this.focusSearch}
                onBlur={this.blurSearch}
                onChange={this.bindSearchTerm}
                value={this.state.searchTerm}
                fullWidth={true}
                InputProps={{
                  disableUnderline: !this.state.isSearchActive,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </div>
            <div className="Entities__header__create">
              <QuiqButton
                onClick={this.toggleCreateEntityModal}
                color="green"
                className="Entities__create"
              >
                <img src={PlusIcon} className="Entities__create__plus-icon" />
                Create New Entity
              </QuiqButton>
              <ReactPaginate
                previousLabel={"Back"}
                nextLabel={"Next"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                initialPage={0}
                forcePage={this.props.currentPage}
                pageCount={this.pageCount() || 0}
                marginPagesDisplayed={1}
                pageRangeDisplayed={5}
                onPageChange={this.updatePage}
                containerClassName={"pagination"}
                subContainerClassName={"pages pagination"}
                activeClassName={"active"}
              />
            </div>
          </div>
        </div>
        <div className="Entity__list">{this.renderEntityList()}</div>
        <CreateEntityModal
          isOpen={this.state.isCreateEntityModalOpen}
          toggleModal={this.toggleCreateEntityModal}
        />
      </div>
    );
  }

  private resetSearchTermAndFetch = () => {
    this.setState({ searchTerm: "" }, this.onResetSearchTermFinished);
  };

  private onResetSearchTermFinished = () => {
    this.blurSearch();
    this.fetchNewEntities();
  };

  private bindSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    this.setState({ searchTerm });
    this.fetchNewEntities();
    this.props.updateSearchFunc({ searchTerm });
  };

  private async fetchNewEntities() {
    this.props.getEntities({
      filter: this.state.searchTerm,
      sortColumn: this.state.entitySort.sortColumn,
      sortAscending: this.state.entitySort.sortAscending,
      getEntityListPage
    });
  }

  private expandSearch = () => {
    this.setState({ isSearchActive: true });
  };

  private collapseSearch = () => {
    if (!this.state.isSearchFocused) {
      this.setState({ isSearchActive: false });
    }
  };

  private focusSearch = () => {
    this.setState({ isSearchFocused: true });
  };

  private blurSearch = () => {
    if (!this.state.searchTerm) {
      this.setState({ isSearchActive: false, isSearchFocused: false });
    }
  };

  private updateSort = (sortColumn: ENTITY_COLUMNS) => {
    const sortAscending =
      sortColumn === this.state.entitySort.sortColumn
        ? !this.state.entitySort.sortAscending
        : true;
    const newSort = { sortColumn, sortAscending };
    this.setState({ entitySort: newSort });
    this.props.getEntities({
      filter: this.state.searchTerm,
      sortColumn,
      sortAscending,
      getEntityListPage
    });
  };

  private renderEntityListHeader = () => {
    return (
      <div
        className="Entity__list__header"
        key="header"
        style={this.headerPaddingRight}
      >
        <EntityListColumnHeader
          entityColumn={ENTITY_COLUMNS.NAME}
          className={"Entity__list__header__item"}
          entitySort={this.state.entitySort}
          updateSort={this.updateSort}
        />
        <EntityListColumnHeader
          entityColumn={ENTITY_COLUMNS.CITY}
          className={"Entity__list__header__item"}
          entitySort={this.state.entitySort}
          updateSort={this.updateSort}
        />
        <EntityListColumnHeader
          entityColumn={ENTITY_COLUMNS.COUNTRY}
          className={"Entity__list__header__item"}
          entitySort={this.state.entitySort}
          updateSort={this.updateSort}
        />
        <EntityListColumnHeader
          entityColumn={ENTITY_COLUMNS.INDUSTRY}
          className={"Entity__list__header__item"}
          entitySort={this.state.entitySort}
          updateSort={this.updateSort}
        />
        <EntityListColumnHeader
          entityColumn={ENTITY_COLUMNS.CLIENT_ENTITY_ID}
          className={"Entity__list__header__item"}
          entitySort={this.state.entitySort}
          updateSort={this.updateSort}
        />
      </div>
    );
  };

  private renderEntityList = () => {
    const entities =
      (this.props.entities &&
        this.props.entities.result &&
        this.props.entities.result.data) ||
      [];

    return [
      this.renderEntityListHeader(),
      <div className="Entity__list-content" key="Entity__list-content">
        {isRequesting(actions.getEntities) || !this.props.entities.result ? (
          <div className="Entity__list--loading" key="loading-spinner">
            <LoadingSpinner />
          </div>
        ) : entities.length === 0 ? (
          <div className="Entity__list--no-results" key="no-content">
            <NoEntityResults
              searchTerm={this.state.searchTerm || ""}
              createEntityCallback={this.toggleCreateEntityModal}
            />
          </div>
        ) : (
          entities.map(entity => (
            <EntityListRow
              entity={entity}
              history={this.props.history}
              key={entity.entityId || entity.name}
            />
          ))
        )}
      </div>
    ];
  };
}

const mapStateToProps = (state: State) => {
  return getEntityListPage(state);
};

export default connect(
  selectorsToProps({
    requestProgress: getRequestProgress,
    entities: getEntityListState,
    currentPage: mapStateToProps
  }),
  {
    updateSearchFunc: updateSearch,
    getEntities: getEntities.action,
    showAlertBanner,
    updatePageFunc: updatePage
  }
)(EntityListView);



// WEBPACK FOOTER //
// ./src/entityList/EntityList.tsx